import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { digestLabels, money, timezoneLabel } from '../lib/format';
import { fieldErrors } from '../lib/form-errors';
import { parseTagInput, tagInputError } from '../lib/order-tags';
import { haptic } from '../lib/telegram';
import type { ArtistProfile, NotificationSettings, Service } from '../types';
import {
  ARTIST_LINKS_LIMIT,
  ARTIST_LINK_LABEL_LIMIT,
  LINK_LABEL_SUGGESTIONS,
  hasInvalidLink,
  isSupportedLinkUrl,
  toArtistLinks,
  toDrafts,
  type LinkDraft,
} from '../lib/artist-links';
import { ContactEmailCard } from '../components/contact-email-card';
import { TemplateManager } from '../components/template-manager';
import { ShareProfile } from '../components/share-profile';
import { ImageField } from '../components/image-field';
import { NotificationSettingsForm } from '../components/notification-settings-form';
import {
  Button,
  Card,
  CopyLink,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  Modal,
  PageHeader,
  Select,
  StatusBadge,
} from '../components/ui';

/** Совпадает с серверным пределом: длиннее описание уводит услуги за первый экран. */
const ARTIST_DESCRIPTION_LIMIT = 600;

/** Русские названия полей: по ним ошибка сервера находит своё место в форме. */
const PROFILE_FIELD_LABELS: Record<string, string> = {
  displayName: 'Имя',
  description: 'Описание',
  commissionsStatus: 'Приём заказов',
  calendarVisibility: 'Видимость календаря',
  currencies: 'Валюты',
  timezone: 'Часовой пояс',
  defaultDepositPercent: 'Предоплата, %',
  defaultRevisionLimit: 'Правок в счёт цены',
  minOrderAmount: 'Минимальная сумма заказа',
  links: 'Ссылки',
  termsText: 'Правила работы',
};

const SERVICE_FIELD_LABELS: Record<string, string> = {
  title: 'Название',
  description: 'Описание',
  basePrice: 'Цена от',
  currency: 'Валюта',
  previewImageId: 'Пример работы',
  secondaryImageId: 'Второй пример',
  tags: 'Теги',
};

export function ProfilePage({ profile }: { profile: ArtistProfile }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [serviceOpen, setServiceOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [removing, setRemoving] = useState<Service | null>(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const services = useQuery({
    queryKey: ['services'],
    queryFn: () => api<{ items: Service[]; activeCount: number }>('/services?includeArchived=true'),
  });
  const notifications = useQuery({
    queryKey: ['notification-settings'],
    queryFn: () => api<NotificationSettings>('/notification-settings'),
  });
  const refreshServices = () => {
    haptic('success');
    void queryClient.invalidateQueries({ queryKey: ['services'] });
  };
  const archive = useMutation({
    mutationFn: (id: string) => api<Service>(`/services/${id}/archive`, { method: 'POST' }),
    onSuccess: refreshServices,
  });
  const restore = useMutation({
    mutationFn: (id: string) => api<Service>(`/services/${id}/restore`, { method: 'POST' }),
    onSuccess: refreshServices,
  });
  const media = useMutation({
    mutationFn: (body: {
      avatarImageId?: string | null;
      bannerImageId?: string | null;
      showContactEmail?: boolean;
    }) => api<ArtistProfile>('/artist/profile', { method: 'PATCH', body }),
    onSuccess: (updated) => queryClient.setQueryData(['profile'], updated),
  });
  const publicUrl = profile.publicLink.url ?? `${location.origin}${profile.publicLink.path}`;

  return (
    <div className="profile-page">
      <PageHeader
        eyebrow="Настройки"
        title="Профиль"
        action={
          <Button variant="ghost" onClick={() => setEditing(true)}>
            Изменить
          </Button>
        }
      />
      <Card className="profile-card">
        <div
          className={`profile-card__cover ${profile.bannerUrl ? 'has-image' : ''}`}
          style={profile.bannerUrl ? { backgroundImage: `url(${profile.bannerUrl})` } : undefined}
        >
          {!profile.bannerUrl && (
            <>
              <span />
              <span />
            </>
          )}
        </div>
        <div className="profile-card__body">
          <div className="profile-avatar">
            {profile.avatarUrl ? (
              <img src={profile.avatarUrl} alt="" />
            ) : (
              initials(profile.displayName)
            )}
          </div>
          <div className="profile-card__identity">
            <h2>{profile.displayName}</h2>
            <p>{profile.description || 'Добавьте описание своего стиля и работ.'}</p>
            <StatusBadge
              tone={
                profile.commissionsStatus === 'OPEN'
                  ? 'success'
                  : profile.commissionsStatus === 'WAITLIST'
                    ? 'warning'
                    : 'neutral'
              }
            >
              {profile.commissionsStatus === 'OPEN'
                ? 'Принимаю заказы'
                : profile.commissionsStatus === 'WAITLIST'
                  ? 'Лист ожидания'
                  : 'Приём закрыт'}
            </StatusBadge>
          </div>
        </div>
      </Card>

      <div className="section-heading">
        <h2>Оформление</h2>
      </div>
      {media.error && <ErrorNotice error={media.error} />}
      {/* Наложение аватара на баннер живёт в карточке профиля выше — она и есть
          предпросмотр. В самом редакторе поля идут друг за другом: там у каждого
          своя подпись, подсказка и кнопка «Убрать», и они не должны наезжать. */}
      <Card className="stack">
        <ImageField
          label="Баннер"
          hint="Широкая картинка в шапке вашей страницы"
          kind="banner"
          shape="wide"
          url={profile.bannerUrl}
          disabled={media.isPending}
          onChange={(bannerImageId) => media.mutate({ bannerImageId })}
        />
        <ImageField
          label="Аватар"
          hint="Квадратный кадр — видно в поиске и в карточке заказа"
          kind="avatar"
          shape="circle"
          url={profile.avatarUrl}
          disabled={media.isPending}
          onChange={(avatarImageId) => media.mutate({ avatarImageId })}
        />
      </Card>

      {profile.links.length > 0 && (
        <>
          <div className="section-heading">
            <h2>Ссылки</h2>
          </div>
          <div className="link-list">
            {profile.links.map((link) => (
              <a
                key={link.url}
                className="link-chip"
                href={link.url}
                target="_blank"
                rel="noreferrer noopener"
              >
                {link.label}
              </a>
            ))}
          </div>
        </>
      )}

      <div className="section-heading">
        <h2>Публичная страница</h2>
      </div>
      <CopyLink url={publicUrl} />
      <ShareProfile profile={profile} publicUrl={publicUrl} />

      <TemplateManager />

      <div className="section-heading">
        <h2>Связь</h2>
      </div>
      <ContactEmailCard
        role="artist"
        publicToggle={{
          value: profile.showContactEmail,
          disabled: media.isPending,
          onChange: (showContactEmail) => media.mutate({ showContactEmail }),
        }}
      />

      <div className="section-heading">
        <h2>Уведомления</h2>
        <button onClick={() => setNotificationsOpen(true)}>Настроить</button>
      </div>
      <Card>
        {/* Сохранённые настройки видны прямо в профиле: иначе после закрытия
            окна непонятно, применилось ли что-нибудь. */}
        {notifications.data ? (
          <div className="detail-grid">
            <div>
              <span>Доставка</span>
              <strong>
                {notifications.data.aroundTheClock
                  ? 'Круглосуточно'
                  : `${notifications.data.sendFromLabel}–${notifications.data.sendToLabel}`}
              </strong>
            </div>
            <div>
              <span>Статусы и этапы</span>
              <strong>{notifications.data.statusUpdates ? 'Приходят' : 'Выключены'}</strong>
            </div>
            <div>
              <span>Дедлайны</span>
              <strong>
                {notifications.data.deadlineReminders
                  ? deadlineLead(notifications.data.deadlineLeadDays)
                  : 'Выключены'}
              </strong>
            </div>
            <div>
              <span>Заказы в работе</span>
              <strong>{digestLabels[notifications.data.digest]}</strong>
            </div>
          </div>
        ) : (
          <p className="muted">
            Выберите, о чём вам писать и когда: смена этапов, приближающиеся дедлайны, сводка по
            работе. Время считаем по вашему часовому поясу из профиля.
          </p>
        )}
      </Card>

      <div className="section-heading">
        <h2>Услуги</h2>
        <button onClick={() => setServiceOpen(true)}>+ Добавить</button>
      </div>
      {services.error && <ErrorNotice error={services.error} />}
      {services.data?.items.length === 0 && (
        <EmptyState
          title="Нет услуг"
          text="Опишите, что вы рисуете и почём, — заказчику останется выбрать нужное в заявке."
          action={<Button onClick={() => setServiceOpen(true)}>Создать услугу</Button>}
        />
      )}
      {(archive.error || restore.error) && <ErrorNotice error={archive.error ?? restore.error} />}
      <div className="list">
        {services.data?.items.map((service) => (
          <Card key={service.id} className={`service-card ${service.isArchived ? 'is-muted' : ''}`}>
            {service.previewUrl && (
              <img className="service-card__preview" src={service.previewUrl} alt="" />
            )}
            <div className="card-row">
              <StatusBadge tone={service.isActive && !service.isArchived ? 'success' : 'neutral'}>
                {service.isArchived ? 'В архиве' : service.isActive ? 'Опубликована' : 'Скрыта'}
              </StatusBadge>
              {service.basePrice && <strong>{money(service.basePrice, service.currency)}</strong>}
            </div>
            <h2>{service.title}</h2>
            <p>{service.description || 'Без описания'}</p>
            {service.tags.length > 0 && (
              <div className="tag-list" aria-label="Теги услуги">
                {service.tags.map((tag) => (
                  <span key={tag} className="tag-pill">
                    #{tag}
                  </span>
                ))}
              </div>
            )}
            <div className="card-actions">
              <Button variant="ghost" onClick={() => setEditingService(service)}>
                Изменить
              </Button>
              {service.isArchived ? (
                <Button
                  variant="ghost"
                  disabled={restore.isPending}
                  onClick={() => restore.mutate(service.id)}
                >
                  Вернуть из архива
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  disabled={archive.isPending}
                  onClick={() => archive.mutate(service.id)}
                >
                  Архивировать
                </Button>
              )}
              <Button variant="danger" onClick={() => setRemoving(service)}>
                Удалить
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <div className="section-heading">
        <h2>Условия</h2>
        <button onClick={() => setEditing(true)}>Изменить</button>
      </div>
      <Card className="detail-grid">
        <div>
          <span>{profile.currencies.length > 1 ? 'Валюты' : 'Валюта'}</span>
          <strong>{profile.currencies.join(' · ')}</strong>
        </div>
        <div>
          <span>Предоплата</span>
          <strong>{depositSplit(profile.defaultDepositPercent)}</strong>
        </div>
        <div>
          <span>Рассрочка</span>
          <strong>
            {profile.installmentsEnabled ? `До ${profile.installmentParts} частей` : 'Нет'}
          </strong>
        </div>
        <div>
          <span>Правки</span>
          <strong>{profile.defaultRevisionLimit}</strong>
        </div>
        <div>
          <span>Заказы от</span>
          <strong>
            {profile.minOrderAmount
              ? money(profile.minOrderAmount, profile.currency)
              : 'Любой суммы'}
          </strong>
        </div>
        <div>
          <span>Напоминать об оплате</span>
          <strong>
            {profile.paymentReminderDays > 0 ? `Раз в ${profile.paymentReminderDays} дн.` : 'Нет'}
          </strong>
        </div>
        <div>
          <span>Календарь заказов</span>
          <strong>{calendarVisibilityLabel(profile.calendarVisibility)}</strong>
        </div>
        <div>
          <span>Часовой пояс</span>
          <strong>{timezoneLabel(profile.timezone)}</strong>
        </div>
      </Card>

      {editing && <ProfileModal profile={profile} onClose={() => setEditing(false)} />}
      {serviceOpen && (
        <ServiceModal currencies={profile.currencies} onClose={() => setServiceOpen(false)} />
      )}
      {editingService && (
        <ServiceModal
          currencies={profile.currencies}
          service={editingService}
          onClose={() => setEditingService(null)}
        />
      )}
      {removing && <DeleteServiceModal service={removing} onClose={() => setRemoving(null)} />}
      {notificationsOpen && (
        <Modal title="Уведомления" onClose={() => setNotificationsOpen(false)}>
          <NotificationSettingsForm
            showTimezone={false}
            onSaved={() => setNotificationsOpen(false)}
          />
        </Modal>
      )}
    </div>
  );
}

/** Предоплата показывается схемой разбивки: сколько сразу и сколько после. */
function depositSplit(depositPercent: number): string {
  return `${depositPercent}/${100 - depositPercent}`;
}

function calendarVisibilityLabel(visibility: ArtistProfile['calendarVisibility']): string {
  if (visibility === 'PUBLIC') return 'Видно всем';
  if (visibility === 'CUSTOMERS') return 'Видно заказчикам';
  return 'Приватный';
}

function deadlineLead(days: number): string {
  return days === 0 ? 'В день дедлайна' : `За ${days} дн.`;
}

function DeleteServiceModal({ service, onClose }: { service: Service; onClose: () => void }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => api<void>(`/services/${service.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      haptic('success');
      void queryClient.invalidateQueries({ queryKey: ['services'] });
      onClose();
    },
  });

  return (
    <Modal title="Удалить услугу" onClose={onClose}>
      <div className="stack">
        <p className="muted">
          «{service.title}» исчезнет со страницы навсегда. Если по услуге уже приходили заявки, её
          получится только убрать в архив — история заказов важнее.
        </p>
        {mutation.error && <ErrorNotice error={mutation.error} />}
        <Button
          variant="danger"
          wide
          disabled={mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? 'Удаляем…' : 'Удалить навсегда'}
        </Button>
        <Button variant="ghost" wide onClick={onClose}>
          Отмена
        </Button>
      </div>
    </Modal>
  );
}

function ProfileModal({ profile, onClose }: { profile: ArtistProfile; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState(profile.displayName);
  const [description, setDescription] = useState(profile.description ?? '');
  const [status, setStatus] = useState(profile.commissionsStatus);
  const [calendarVisibility, setCalendarVisibility] = useState(profile.calendarVisibility);
  const [terms, setTerms] = useState(profile.termsText ?? '');
  const [currencies, setCurrencies] = useState(profile.currencies.join(', '));
  const [timezone, setTimezone] = useState(profile.timezone);
  const [deposit, setDeposit] = useState(String(profile.defaultDepositPercent));
  const [revisions, setRevisions] = useState(String(profile.defaultRevisionLimit));
  const [links, setLinks] = useState<LinkDraft[]>(() => toDrafts(profile.links));
  const [installments, setInstallments] = useState(profile.installmentsEnabled);
  const [installmentParts, setInstallmentParts] = useState(String(profile.installmentParts));
  const [reminderDays, setReminderDays] = useState(String(profile.paymentReminderDays));
  const [minAmount, setMinAmount] = useState(profile.minOrderAmount ?? '');
  const mutation = useMutation({
    mutationFn: () =>
      api<ArtistProfile>('/artist/profile', {
        method: 'PATCH',
        body: {
          displayName,
          description: description || null,
          termsText: terms || null,
          commissionsStatus: status,
          calendarVisibility,
          // Разбор перечисления оставлен серверу: там же лежит проверка кодов.
          currencies,
          timezone,
          defaultDepositPercent: Number(deposit),
          defaultRevisionLimit: Number(revisions),
          installmentsEnabled: installments,
          installmentParts: Number(installmentParts),
          paymentReminderDays: Number(reminderDays),
          minOrderAmount: minAmount.trim().replace(',', '.') || null,
          links: toArtistLinks(links),
        },
      }),
    onSuccess: (updated) => {
      haptic('success');
      queryClient.setQueryData(['profile'], updated);
      onClose();
    },
  });
  const errors = fieldErrors(mutation.error);

  return (
    <Modal title="Редактировать профиль" onClose={onClose}>
      <form
        // Ошибки показывают поля формы: браузерная подсказка дёргала лист.
        noValidate
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          mutation.mutate();
        }}
      >
        <Field label="Имя" error={errors.displayName}>
          <Input value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
        </Field>
        <Field
          label="Описание"
          hint={`${description.length}/${ARTIST_DESCRIPTION_LIMIT} · пустая строка делит текст на абзацы`}
          error={
            description.length > ARTIST_DESCRIPTION_LIMIT
              ? `Не больше ${ARTIST_DESCRIPTION_LIMIT} символов`
              : errors.description
          }
        >
          <textarea
            className="input input--textarea input--tall"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
        <Field label="Приём заказов" error={errors.commissionsStatus}>
          <Select
            value={status}
            onChange={(event) => setStatus(event.target.value as typeof status)}
          >
            <option value="OPEN">Открыт</option>
            <option value="WAITLIST">Лист ожидания</option>
            <option value="CLOSED">Закрыт</option>
          </Select>
        </Field>
        <Field
          label="Видимость календаря"
          hint="Заказчик увидит только занятые даты, без названий заказов"
          error={errors.calendarVisibility}
        >
          <Select
            value={calendarVisibility}
            onChange={(event) =>
              setCalendarVisibility(event.target.value as typeof calendarVisibility)
            }
          >
            <option value="PUBLIC">Видно всем</option>
            <option value="CUSTOMERS">Видно заказчикам</option>
            <option value="PRIVATE">Приватный</option>
          </Select>
        </Field>
        <div className="form-grid">
          <Field label="Валюты" hint="Через запятую. Первая — основная" error={errors.currencies}>
            <Input
              value={currencies}
              placeholder="RUB, USD"
              onChange={(event) => setCurrencies(event.target.value)}
            />
          </Field>
          <Field label="Часовой пояс" hint="Регион или сдвиг: +7" error={errors.timezone}>
            <Input
              value={timezone}
              placeholder="Europe/Moscow"
              onChange={(event) => setTimezone(event.target.value)}
            />
          </Field>
        </div>
        <div className="form-grid">
          <Field
            label="Предоплата, %"
            hint={`Разбивка ${depositSplit(Number(deposit) || 0)}`}
            error={inRange(deposit, 100) ? errors.defaultDepositPercent : 'От 0 до 100'}
          >
            <Input
              type="number"
              min={0}
              max={100}
              value={deposit}
              onChange={(event) => setDeposit(event.target.value)}
            />
          </Field>
          <Field
            label="Правок в счёт цены"
            error={inRange(revisions, 50) ? errors.defaultRevisionLimit : 'От 0 до 50'}
          >
            <Input
              type="number"
              min={0}
              max={50}
              value={revisions}
              onChange={(event) => setRevisions(event.target.value)}
            />
          </Field>
        </div>
        <Field
          label={`Минимальная сумма заказа, ${profile.currency}`}
          hint="Заявки с меньшим бюджетом не принимаются. Пусто — без порога принятия"
          error={isMoney(minAmount) ? errors.minOrderAmount : 'Сумма вида 5000 или 5000.50'}
        >
          <Input
            inputMode="decimal"
            value={minAmount}
            placeholder="Без ограничения"
            onChange={(event) => setMinAmount(event.target.value)}
          />
        </Field>
        <label className="toggle-row">
          <span>Рассрочка</span>
          <button
            type="button"
            role="switch"
            aria-checked={installments}
            aria-label="Рассрочка"
            className={`switch ${installments ? 'is-on' : ''}`}
            onClick={() => setInstallments(!installments)}
          >
            <span />
          </button>
        </label>
        {installments && (
          <Field
            label="Частей в рассрочке"
            hint="На сколько частей максимум согласны разбить оплату"
            error={inRange(installmentParts, 12, 2) ? errors.installmentParts : 'От 2 до 12'}
          >
            <Input
              type="number"
              min={2}
              max={12}
              value={installmentParts}
              onChange={(event) => setInstallmentParts(event.target.value)}
            />
          </Field>
        )}
        <Field
          label="Напоминать об оплате раз в, дней"
          hint="0 — не напоминать"
          error={inRange(reminderDays, 60) ? errors.paymentReminderDays : 'От 0 до 60'}
        >
          <Input
            type="number"
            min={0}
            max={60}
            value={reminderDays}
            onChange={(event) => setReminderDays(event.target.value)}
          />
        </Field>
        <LinkFields links={links} error={errors.links} onChange={setLinks} />
        <Field label="Правила работы" error={errors.termsText}>
          <textarea
            className="input input--textarea"
            value={terms}
            onChange={(event) => setTerms(event.target.value)}
          />
        </Field>
        {mutation.error && <ErrorNotice error={mutation.error} labels={PROFILE_FIELD_LABELS} />}
        <Button
          type="submit"
          wide
          disabled={
            !displayName.trim() ||
            description.length > ARTIST_DESCRIPTION_LIMIT ||
            !inRange(deposit, 100) ||
            !inRange(revisions, 50) ||
            !inRange(reminderDays, 60) ||
            !isMoney(minAmount) ||
            (installments && !inRange(installmentParts, 12, 2)) ||
            hasInvalidLink(links) ||
            mutation.isPending
          }
        >
          Сохранить
        </Button>
      </form>
    </Modal>
  );
}

/**
 * Ссылки художника: подпись он придумывает сам, потому что кроме сайта и
 * Hipolink это бывает галерея, магазин или соцсеть с любым названием. Пустая
 * подпись при сохранении заменяется доменом.
 */
function LinkFields({
  links,
  error,
  onChange,
}: {
  links: LinkDraft[];
  error?: string;
  onChange: (next: LinkDraft[]) => void;
}) {
  const update = (index: number, patch: Partial<LinkDraft>) =>
    onChange(links.map((link, current) => (current === index ? { ...link, ...patch } : link)));

  return (
    <div className="link-fields">
      <div className="link-fields__head">
        <span className="field__label">Ссылки</span>
        {links.length < ARTIST_LINKS_LIMIT && (
          <button type="button" onClick={() => onChange([...links, { label: '', url: '' }])}>
            + Ещё
          </button>
        )}
      </div>

      {links.map((link, index) => {
        const invalid = link.url.trim().length > 0 && !isSupportedLinkUrl(link.url);

        return (
          <div className="link-fields__row" key={index}>
            <Input
              className="link-fields__label"
              value={link.label}
              maxLength={ARTIST_LINK_LABEL_LIMIT}
              placeholder={LINK_LABEL_SUGGESTIONS[index] ?? 'Название'}
              aria-label="Название ссылки"
              onChange={(event) => update(index, { label: event.target.value })}
            />
            <Input
              inputMode="url"
              value={link.url}
              placeholder="https://example.com"
              aria-label="Адрес ссылки"
              onChange={(event) => update(index, { url: event.target.value })}
            />
            {links.length > 1 && (
              <button
                type="button"
                className="link-fields__remove"
                aria-label="Убрать ссылку"
                onClick={() => onChange(links.filter((_, current) => current !== index))}
              >
                ×
              </button>
            )}
            {invalid && <span className="field__error">Ссылка должна начинаться с https://</span>}
          </div>
        );
      })}

      {error ? (
        <span className="field__error">{error}</span>
      ) : (
        <span className="field__hint">
          Название придумываете сами: галерея, магазин или соцсеть. Пустое — подставим домен
        </span>
      )}
    </div>
  );
}

/**
 * Одна форма на создание и редактирование: поля совпадают, отличается только
 * метод запроса. Пример работы сохраняется вместе с остальными полями.
 */
function ServiceModal({
  currencies,
  service,
  onClose,
}: {
  currencies: string[];
  service?: Service;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(service?.title ?? '');
  const [description, setDescription] = useState(service?.description ?? '');
  const [price, setPrice] = useState(service?.basePrice ?? '');
  const [currency, setCurrency] = useState(service?.currency ?? currencies[0] ?? 'RUB');
  const [previewImageId, setPreviewImageId] = useState(service?.previewImageId ?? null);
  const [previewUrl, setPreviewUrl] = useState(service?.previewUrl ?? null);
  const [secondaryImageId, setSecondaryImageId] = useState(service?.secondaryImageId ?? null);
  const [secondaryUrl, setSecondaryUrl] = useState(service?.secondaryUrl ?? null);
  const [tagInput, setTagInput] = useState((service?.tags ?? []).join(', '));
  const parsedTags = parseTagInput(tagInput);
  const tagsError = tagInputError(tagInput);
  const mutation = useMutation({
    mutationFn: () =>
      api<Service>(service ? `/services/${service.id}` : '/services', {
        method: service ? 'PATCH' : 'POST',
        body: {
          title,
          description: description || null,
          basePrice: price || null,
          currency,
          previewImageId,
          secondaryImageId,
          tags: parsedTags,
          ...(service ? {} : { isActive: true }),
        },
      }),
    onSuccess: () => {
      haptic('success');
      void queryClient.invalidateQueries({ queryKey: ['services'] });
      onClose();
    },
  });

  const errors = fieldErrors(mutation.error);

  return (
    <Modal title={service ? 'Услуга' : 'Новая услуга'} onClose={onClose}>
      <form
        // Ошибки показывают поля формы: браузерная подсказка дёргала лист.
        noValidate
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          mutation.mutate();
        }}
      >
        <Field label="Название" error={errors.title}>
          <Input
            required
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Портрет"
          />
        </Field>
        <Field label="Описание" error={errors.description}>
          <textarea
            className="input input--textarea"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
        <ImageField
          label="Пример работы"
          hint="Обложка услуги — видна в поиске"
          kind="cover"
          url={previewUrl}
          onChange={(imageId) => {
            setPreviewImageId(imageId);
            setPreviewUrl(imageId ? `/v1/images/${imageId}` : null);
          }}
        />
        {/* Второй пример: больше двух услуга не показывает — карточка должна читаться. */}
        <ImageField
          label="Второй пример"
          hint="Необязательно. Второй кадр встанет рядом с обложкой"
          kind="cover"
          url={secondaryUrl}
          onChange={(imageId) => {
            setSecondaryImageId(imageId);
            setSecondaryUrl(imageId ? `/v1/images/${imageId}` : null);
          }}
        />
        <Field
          label="Теги услуги"
          hint={`${parsedTags.length}/8 · через запятую. По ним услугу находят в поиске`}
          error={tagsError ?? errors.tags}
        >
          <Input
            value={tagInput}
            onChange={(event) => setTagInput(event.target.value)}
            placeholder="портрет, фэнтези, чиби"
          />
        </Field>
        {currencies.length > 1 ? (
          <div className="form-grid">
            <Field label="Цена от" error={errors.basePrice}>
              <Input
                inputMode="decimal"
                value={price}
                onChange={(event) => setPrice(event.target.value)}
              />
            </Field>
            <Field label="Валюта" error={errors.currency}>
              <Select value={currency} onChange={(event) => setCurrency(event.target.value)}>
                {currencies.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        ) : (
          <Field label={`Цена от, ${currency}`} error={errors.basePrice}>
            <Input
              inputMode="decimal"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
            />
          </Field>
        )}
        {mutation.error && <ErrorNotice error={mutation.error} labels={SERVICE_FIELD_LABELS} />}
        <Button
          type="submit"
          wide
          disabled={!title.trim() || Boolean(tagsError) || mutation.isPending}
        >
          {service ? 'Сохранить' : 'Создать'}
        </Button>
      </form>
    </Modal>
  );
}

/** Пустое поле и текст — не число: такие значения сервер всё равно отклонит. */
/** Пустая строка допустима: она означает «порога нет» и уходит на сервер как `null`. */
function isMoney(value: string): boolean {
  return value.trim() === '' || /^\d{1,10}([.,]\d{1,2})?$/.test(value.trim());
}

function inRange(value: string, max: number, min = 0): boolean {
  const parsed = Number(value);
  return value.trim() !== '' && Number.isInteger(parsed) && parsed >= min && parsed <= max;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}
